import { warn } from 'cc';
import BettingModel from './BettingModel';
import StateModel from '../../../../../common/scripts/Toolkit/Architecture/StateSynchronization/StateModel';
import IdleTimer from '../../../../../common/scripts/Toolkit/Helper/IdleTimer';
import Delay from '../../../../../common/scripts/Toolkit/Utils/Delay';
import DataType from '../Config/DataStructure';
import Result from '../DataObject/Result';
import { PathName } from '../Config/PathConfig';
import TableGame from '../../../System/TableGame';
import ITableService from '../Service/ITableService';

enum TableEventType {
    ENTER_TABLE = 'ENTER_TABLE',
    EXIT_TABLE = 'EXIT_TABLE',
    IDLE_TIMEOUT_CHANGED = 'IDLE_TIMEOUT_CHANGED',
    HOLDING_WIN_STATE_CHANGED = 'HOLDING_WIN_STATE_CHANGED',
    RECEIVE_RESULT = 'RECEIVE_RESULT',
    ROUND_ID_CHANGED = 'ROUND_ID_CHANGED',
    PLAYER_COUNT_CHANGED = 'ONLINE_PLAYER_CHANGED',
    STREAMING_URL_CHANGED = 'STREAMING_URL_CHANGED',
    MACHINE_STATE_CHANGED = 'MACHINE_STATE_CHANGED',
}

export enum MachineState {
    Ready,
    Playing,
    Maintenance,
    ComingSoon,
    Idle,
}

export enum PlayType {
    Single,
    Multiple,
    Round,
    Crowd,

    // coming soon
    Stage,
    Party,
    MiniGame,
}

export namespace PlayType {
    const playTypeMap: { [value in PlayType]: DataType.Enums.PlayType } & { [hallType in DataType.Enums.PlayType]: PlayType } = {
        [PlayType.Single]: DataType.Enums.PlayType.Single,
        [PlayType.Multiple]: DataType.Enums.PlayType.Multiple,
        [PlayType.Round]: DataType.Enums.PlayType.Round,
        [PlayType.Crowd]: DataType.Enums.PlayType.Crowd,
        [PlayType.Stage]: DataType.Enums.PlayType.Stage,
        [PlayType.Party]: DataType.Enums.PlayType.Party,
        [PlayType.MiniGame]: DataType.Enums.PlayType.MiniGame,
        [DataType.Enums.PlayType.Single]: PlayType.Single,
        [DataType.Enums.PlayType.Multiple]: PlayType.Multiple,
        [DataType.Enums.PlayType.Round]: PlayType.Round,
        [DataType.Enums.PlayType.Crowd]: PlayType.Crowd,
        [DataType.Enums.PlayType.Stage]: PlayType.Stage,
        [DataType.Enums.PlayType.Party]: PlayType.Party,
        [DataType.Enums.PlayType.MiniGame]: PlayType.MiniGame,
    };
    export function map(name: DataType.Enums.PlayType): PlayType;
    export function map(type: PlayType): DataType.Enums.PlayType;
    export function map(arg: PlayType | DataType.Enums.PlayType): DataType.Enums.PlayType | PlayType {
        return playTypeMap[arg];
    }
    Object.defineProperty(PlayType, 'map', {
        enumerable: false,
    });
}

export const CAN_PLAY_MACHINE_STATES = [MachineState.Idle, MachineState.Ready, MachineState.Playing];

export default class TableModel extends StateModel {
    public static readonly EVENT_TYPE = { ...TableEventType, ...StateModel.EVENT_TYPE };

    // info
    public readonly type: DataType.Enums.PlayType = null;
    public readonly hallID: number = -1;
    public readonly gameID: string = '';
    public readonly tableID: number = -1;
    public readonly isTrial: boolean = false;
    public readonly machineName: string = '';

    // model
    protected readonly _bettingModel: BettingModel = null;

    protected _holdingWin = false;
    protected _result: Result = null;
    protected _streamingInfoList: DataType.Table.StreamData['Streams'] = [];
    protected _idleTimeThreshold: number = -1;
    protected _requestStreamingDataTimes: number = 0;

    // state
    protected _roundCount = -1;
    protected _roundID = -1;
    protected _playerCount = 0;
    protected _state: DataType.Enums.TableState = null;
    protected _previousState: DataType.Enums.TableState = null;
    protected _machineState: MachineState = MachineState.Ready;
    protected _playing = false;
    protected _closed = false;
    protected _isMaintaining = false;
    protected _receiveEventTime = 0;
    protected _isCurrent = false;
    // idle state
    protected _idleTimer: IdleTimer = new IdleTimer(true);
    protected _idleTimeout: number = -1;
    protected _idleTimeOffet: number = 0;
    protected _lastWinNumber = 0;

    constructor(
        data: DataType.Table.SummaryData,
        protected readonly _tableService: ITableService,
    ) {
        super();
        this.init();
        this._idleTimer.on(IdleTimer.EVENT_TYPE.ELAPSED_TIME_CHANGED, () => {
            this.emit(TableModel.EVENT_TYPE.IDLE_TIMEOUT_CHANGED);
        });

        // betting model
        this._bettingModel = this._tableService.getBettingModel(this);

        // info
        // this.type = PlayType.map(data.HallID);
        this.type = DataType.Enums.PlayType.Single;
        this.hallID = data.HallID;
        this.tableID = data.TableID;
        this.gameID = data.GameID;
        this.isTrial = data.IsTrial;
        // HACK
        this.machineName = TableGame.i18n.getMessage(data.GameID.toUpperCase());
        this.updateWithSummaryData(data);
    }

    public get bettingModel() {
        return this._bettingModel;
    }

    public get isCurrent() {
        return this._isCurrent;
    }

    public get isIdle() {
        return this._idleTimer.isIdle;
    }

    public get holdingWin() {
        return this._holdingWin;
    }
    protected set holdingWin(flag: boolean) {
        if (flag === this._holdingWin) {
            return;
        }
        this._holdingWin = flag;
        this.emit(TableModel.EVENT_TYPE.HOLDING_WIN_STATE_CHANGED);
    }

    public get roundID() {
        return this._roundID;
    }
    protected set roundID(value: number) {
        if (this._roundID === value) {
            return;
        }
        this._roundID = value;
        this.emit(TableModel.EVENT_TYPE.ROUND_ID_CHANGED);
    }

    public get state() {
        return this._state;
    }
    protected set state(newState: DataType.Enums.TableState) {
        if (newState === this._state) {
            return;
        }
        this._state = newState;
        this.onStateChanged();
        this.emit(TableModel.EVENT_TYPE.STATE_CHANGED);
    }

    public get machineState() {
        return this._machineState;
    }
    protected set machineState(newState: MachineState) {
        if (newState === this._machineState) {
            return;
        }
        this._machineState = newState;
        this.emit(TableModel.EVENT_TYPE.MACHINE_STATE_CHANGED);
    }

    public get previousState() {
        return this._previousState;
    }

    public get playerCount() {
        return this._playerCount;
    }
    private set playerCount(value: number) {
        if (this._playerCount === value) {
            return;
        }
        this._playerCount = value;
        this.emit(TableModel.EVENT_TYPE.PLAYER_COUNT_CHANGED);
    }

    public get playing() {
        return this._playing;
    }

    public get closed() {
        return this._closed;
    }

    public get result() {
        return this._result;
    }
    protected set result(value: Result) {
        if (value === this._result) {
            return;
        }
        this._result = value;
        if (value) {
            if (value.totalWin) {
                // holding win when win amount is not 0
                this.holdingWin = true;
            }
            this.emit(TableModel.EVENT_TYPE.RECEIVE_RESULT);
        } else {
            this.holdingWin = false;
        }
    }

    public get streamingInfoList() {
        return this._streamingInfoList;
    }
    protected set streamingInfoList(infoList: DataType.Table.StreamData['Streams']) {
        if (infoList === this._streamingInfoList) {
            return;
        }
        this._streamingInfoList = infoList ?? [];
        if (this._streamingInfoList.length < 2) {
            if (this._requestStreamingDataTimes < 5) {
                Delay.resolve(1000).then(() => this.requestStreamData());
                this._requestStreamingDataTimes++;
            } else {
                warn('Streaming data is insufficient.');
            }
        } else {
            this._requestStreamingDataTimes = 0;
        }
        this.emit(TableModel.EVENT_TYPE.STREAMING_URL_CHANGED);
    }

    public get idleTimeThreshold() {
        return this._idleTimeThreshold;
    }

    public get idleTimeout() {
        return this._idleTimeout - (this._idleTimer.isIdle ? this._idleTimer.elapsedTime * 1000 + this._idleTimeOffet : 0);
    }
    protected set idleTimeout(value: number) {
        if (this._idleTimeout === value) {
            return;
        }
        this._idleTimeout = value;
        this.emit(TableModel.EVENT_TYPE.IDLE_TIMEOUT_CHANGED);
    }

    public get lastWinNumber() {
        return this._lastWinNumber;
    }

    public set lastWinNumber(value: number) {
        if (this._lastWinNumber === value) {
            return;
        }
        this._lastWinNumber = value;
    }

    public get duringPlaying() {
        return this.machineState === MachineState.Playing || this.machineState === MachineState.Ready;
    }

    public get roundIndex() {
        return this._roundCount - 1;
    }

    public init() {
        TableGame.connection.on(PathName.TableSummary, this.updateWithSummaryData, this);
        TableGame.connection.on(PathName.PlayerStatus, this.updateIdleTimeout, this);
    }

    public updateWithSummaryData(data: DataType.Table.SummaryData) {
        if (this.tableID !== data.TableID) {
            return;
        }
        this._playing = data.Playing;
        this._closed = data.Closed;
        this.updateMachineState();
    }

    // model operations
    public async enter(data: DataType.Table.EnterResponseData) {
        await this.requestDatas();
        this._isCurrent = true;
        this.bettingModel.initConfig(data);
        this._idleTimeThreshold = data.WarnIdleTime ?? Number.MAX_SAFE_INTEGER;
        this.updateIdleState();
        // generate result from entry data if in showing state
        if (this.state === DataType.Enums.TableState.Showing) {
            this._result = this._tableService.getResult(data.LastResult);
        }
        this.updateWithSummaryData(data);
        this.emit(TableModel.EVENT_TYPE.ENTER_TABLE, data);
    }

    public exit(exitManually: boolean, exitTableReason?: string) {
        if (!this._isCurrent) {
            return;
        }
        this._isCurrent = false;
        this.emit(TableModel.EVENT_TYPE.EXIT_TABLE, exitManually, exitTableReason);
    }

    public stopHoldingWin() {
        this.holdingWin = false;
    }

    public async requestStreamData() {
        const data = await TableGame.connection.send(PathName.TableStream);
        this.streamingInfoList = data.Streams;
    }

    protected async requestDatas() {
        await Promise.all([this.requestStreamData()]);
    }

    protected onStateChanged() {
        this.updateIdleState();
    }

    protected updateMachineState() {
        if (this._closed) {
            this.machineState = MachineState.Maintenance;
            return;
        }
        this.machineState = this._playing ? MachineState.Playing : MachineState.Idle;
    }

    protected updateIdleState() {
        if (this.state === DataType.Enums.TableState.Betting) {
            // clear result from previous round and start counting idle time
            this._idleTimer.isIdle = true;
            this._idleTimer.elapsedTime = 0;
        } else {
            this._idleTimer.isIdle = false;
        }
    }

    protected updateIdleTimeout(data: DataType.Table.PlayerStatusData) {
        let idleTimeout: number;
        if (data.RemainingIdleTime !== undefined) {
            idleTimeout = data.RemainingIdleTime;
            this._idleTimeOffet = data.Time ? Date.now() - data.Time : 0;
        } else {
            return;
        }
        this.idleTimeout = idleTimeout;
    }
}
